/**
 * DEPRECATED
 * This component was built originally for uncontrolled forms but now we are moving towards
 * controlled forms because they are a more common React pattern. For more info check the link.
 * @link https://benchco.atlassian.net/browse/IN-2122?utm_medium=referral-external&utm_source=jira-slack&utm_term=
 */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';

import NotificationMessage from './NotificationMessage';

// Padding that separates the error message from the input above
const NotificationMessageWrapper = styled.div`
  padding-top: 4px;
`;

/**
 * Takes a component that renders an HTML input element that is a candidate for validation according
 * to the Constraint Validation API and shows branded error messages instead of the browser's
 * built-in error bubble popups.
 *
 * Reports validity on changes to the input's value instead of waiting until the form is submitted.
 *
 * Provides a `wasValidated` boolean prop to the component that represents whether validation has
 * been run on the input since being edited. This can improve user experience in that the component
 * can differentiate between inputs that have invalid default values (like an initially empty input
 * that is `required`) so that they can be styled differently. This can prevent pre-populated and
 * empty forms from showing validation issues before the user has interacted with them.
 *
 * The returned component accepts a `validates` prop which can be set to a function that takes the
 * current value and returns a Promise that resolves to a validation error message or the empty
 * string if the value is valid.
 *
 * `withValidation` takes a `refPropName` param that's needed to suppress default validation popups.
 * The name of the property to assign a ref to can differ. Ideally it's "ref" and composite
 * components use ref forwarding to return the ref to the leaf DOM node, but in older libraries
 * (such as styled-components version 2) it's called "innerRef", or something else determined by the
 * component author.
 *
 * @param {React.ComponentType} WrappedComponent - Component that renders an HTML form input element
 * @param {string} refPropName - Prop name used to get a ref to the DOM node for WrappedComponent
 * @return {React.ComponentClass}
 */
const withValidation = (WrappedComponent, refPropName = 'ref') => {
  class WithValidation extends Component {
    static propTypes = {
      // eslint-disable-next-line react/forbid-prop-types
      defaultValue: PropTypes.any,
      onChange: PropTypes.func,
      onInvalid: PropTypes.func,
      validates: PropTypes.func,
    };

    static defaultProps = {
      defaultValue: undefined,
      onChange: undefined,
      onInvalid: undefined,
      validates: undefined,
    };

    constructor(props) {
      super(props);
      this.inputRef = React.createRef();
      this.state = { wasValidated: false };
    }

    componentDidMount() {
      // In some situations, like when a component is rendered by `react-test-renderer`, refs will
      // have their `current` property set to `null`
      if (this.inputRef.current) {
        // "invalid" events on inputs don't bubble, therefore React's synthetic events can't be
        // used for them if `preventDefault()` needs to be called. We do need to call
        // `preventDefault()` in order to suppress the HTML form validation popup bubbles from
        // appearing.
        this.inputRef.current.addEventListener('invalid', this.handleInvalid);
      }
    }

    componentWillUnmount() {
      // In some situations, like when a component is rendered by `react-test-renderer`, refs will
      // have their `current` property set to `null`
      if (this.inputRef.current) {
        // Clean up the manually added event listener from `componentDidMount()`
        this.inputRef.current.removeEventListener('invalid', this.handleInvalid);
      }
    }

    handleChange = changeEvent => {
      // Ensure the wrapped component's `onChange` handler is called if one is defined
      if (typeof this.props.onChange === 'function') this.props.onChange(changeEvent);
      // `target` needs to be cached because React synthetic events are reused and it is referenced
      // in an async callback below
      const { target } = changeEvent;
      // In the event that a form is pre-populated with values (via `defaultValue`) and the default
      // is invalid, custom validity should be ignored because the problem is with the code that
      // sets the default value to an invalid one. This condition will be triggered when a value is
      // changed and then changed again back to the default.
      if (target.value === this.props.defaultValue) {
        target.setCustomValidity('');
        target.reportValidity();
        // Trigger a re-render to ensure that any current error message is cleared from the UI
        this.setState({ wasValidated: true });
        return;
      }
      // If a custom validation function is provided, then get the custom validation message from it
      // before reporting the validity of the input. Custom validation may be asynchronous, which is
      // why `validate` must be a `Promise`.
      const validate =
        typeof this.props.validates === 'function'
          ? this.props.validates(target.value)
          : Promise.resolve('');
      validate.then(validationMessage => {
        target.setCustomValidity(validationMessage);
        // Use the built-in HTML validation API to report validation errors as the user changes the
        // value in the input. Then ensure the state of the component reflects that validation has
        // occurred.
        target.reportValidity();
        this.setState({ wasValidated: true });
      });
    };

    handleInvalid = invalidEvent => {
      // Prevent the default HTML validation message popup bubbles from appearing
      invalidEvent.preventDefault();
      // Ensure the state reflects that validation has occurred
      this.setState({ wasValidated: true }, () => {
        // Ensure the wrapped component's `onInvalid` handler is called if one is defined after the value
        // of wasValidated was set in the state
        if (typeof this.props.onInvalid === 'function') this.props.onInvalid(invalidEvent);
      });
    };

    render() {
      // If the ref has a current DOM node, then use the validationMessage from that node, or
      // default to an empty string.
      const errorMessage = this.inputRef.current ? this.inputRef.current.validationMessage : '';
      return (
        <div>
          <WrappedComponent
            {...{
              // Delegate all props passed to the wrapper component down to the wrapped component
              ...this.props,
              // The prop used to get a ref to a DOM node differs depending on the library its from
              [refPropName]: this.inputRef,
            }}
            onChange={this.handleChange}
            wasValidated={this.state.wasValidated}
          />
          {
            // According to the Constraint Validation API, a valid input will have the empty string
            // as its validation message. So, only if there is content of the validation message
            // should the error message be rendered.
            this.state.wasValidated && !!errorMessage && (
              <NotificationMessageWrapper>
                <NotificationMessage styleType="error">{errorMessage}</NotificationMessage>
              </NotificationMessageWrapper>
            )
          }
        </div>
      );
    }
  }
  return WithValidation;
};

export default withValidation;
